import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import PartySocket from 'partysocket';
import type { OrderType } from '@alphabet-hunter/shared';
import type { GameView, WordFeedback } from '../types';
import { generateGameCode } from '../lib/gameCode';
import { useToast } from './ToastContext';

const PARTYKIT_HOST = import.meta.env.VITE_PARTYKIT_HOST;

interface GameContextValue {
  connected: boolean;
  game: GameView | null;
  gameCode: string | null;
  isHost: boolean;
  joinError: string | null;
  wordFeedback: WordFeedback | null;
  createGame: (playerName: string, orderType: OrderType) => void;
  joinGame: (playerName: string, code: string) => void;
  startGame: () => void;
  submitWord: (word: string) => void;
  leaveGame: () => void;
  clearJoinError: () => void;
  clearWordFeedback: () => void;
}

const GameContext = createContext<GameContextValue | null>(null);

export function GameProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const socketRef = useRef<PartySocket | null>(null);
  const playerNameRef = useRef('');

  const [connected, setConnected] = useState(false);
  const [game, setGame] = useState<GameView | null>(null);
  const [gameCode, setGameCode] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [wordFeedback, setWordFeedback] = useState<WordFeedback | null>(null);

  // Mirrors the pre-migration client's host detection: the server strips
  // connection ids from the public game payload, so this is a name match,
  // not authoritative — the server enforces the real check (connection id)
  // on 'start-game' itself and replies with an error-msg toast if it fails.
  const isHost = useMemo(
    () => !!game?.players.find((p) => p.name === playerNameRef.current)?.isHost,
    [game]
  );

  const handleServerMessage = useCallback(
    (raw: unknown) => {
      if (typeof raw !== 'string') return;
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(raw);
      } catch {
        return;
      }
      const { type, ...payload } = data as { type: string } & Record<string, unknown>;

      switch (type) {
        case 'game-created':
        case 'game-joined': {
          const p = payload as { gameId: string; game: GameView };
          setGameCode(p.gameId);
          setGame(p.game);
          setJoinError(null);
          break;
        }
        case 'join-error': {
          setJoinError((payload as { message: string }).message);
          break;
        }
        case 'lobby-updated':
        case 'game-started':
        case 'game-over': {
          setGame(payload as unknown as GameView);
          break;
        }
        case 'word-accepted': {
          const p = payload as { playerName: string; word: string; letter: string; game: GameView };
          const mine = p.playerName === playerNameRef.current;
          setWordFeedback({
            message: mine
              ? `"${p.word}" — nice find! +1 point`
              : `${p.playerName} found "${p.word}" for ${p.letter}!`,
            type: mine ? 'success' : 'info',
          });
          setGame(p.game);
          break;
        }
        case 'word-rejected': {
          setWordFeedback({ message: (payload as { reason: string }).reason, type: 'error' });
          break;
        }
        case 'error-msg': {
          showToast((payload as { message: string }).message, 3500);
          break;
        }
        // 'player-left' is never actually sent by the server (dead in the
        // pre-migration client too) — intentionally not handled here.
      }
    },
    [showToast]
  );

  const connectToRoom = useCallback(
    (code: string) => {
      socketRef.current?.close();
      const ws = new PartySocket({ host: PARTYKIT_HOST, room: code, party: 'game-room' });
      ws.addEventListener('open', () => setConnected(true));
      ws.addEventListener('close', () => setConnected(false));
      ws.addEventListener('error', () => showToast('Connection failed. Please check your network.', 4000));
      ws.addEventListener('message', (evt) => handleServerMessage(evt.data));
      socketRef.current = ws;
      return ws;
    },
    [handleServerMessage, showToast]
  );

  const createGame = useCallback(
    (playerName: string, orderType: OrderType) => {
      playerNameRef.current = playerName;
      const ws = connectToRoom(generateGameCode());
      ws.addEventListener(
        'open',
        () => ws.send(JSON.stringify({ type: 'create-game', playerName, orderType })),
        { once: true }
      );
    },
    [connectToRoom]
  );

  const joinGame = useCallback(
    (playerName: string, code: string) => {
      playerNameRef.current = playerName;
      const ws = connectToRoom(code);
      ws.addEventListener('open', () => ws.send(JSON.stringify({ type: 'join-game', playerName })), {
        once: true,
      });
    },
    [connectToRoom]
  );

  const startGame = useCallback(() => {
    socketRef.current?.send(JSON.stringify({ type: 'start-game' }));
  }, []);

  const submitWord = useCallback((word: string) => {
    socketRef.current?.send(JSON.stringify({ type: 'submit-word', word }));
  }, []);

  const leaveGame = useCallback(() => {
    socketRef.current?.close();
    socketRef.current = null;
    setConnected(false);
    setGame(null);
    setGameCode(null);
    setJoinError(null);
    setWordFeedback(null);
  }, []);

  const value: GameContextValue = {
    connected,
    game,
    gameCode,
    isHost,
    joinError,
    wordFeedback,
    createGame,
    joinGame,
    startGame,
    submitWord,
    leaveGame,
    clearJoinError: () => setJoinError(null),
    clearWordFeedback: () => setWordFeedback(null),
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used inside GameProvider');
  return ctx;
}
